import { useState } from "react";
import { useToast } from "@/components/base/Toast";

interface NormalizationRule {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  extra?: string;
}

const initialRules: NormalizationRule[] = [
  {
    id: "duplicate-detection",
    title: "Duplicate phone number detection",
    description:
      "If incoming lead phone matches existing lead, merge or flag instead of creating duplicate",
    enabled: true,
  },
  {
    id: "phone-formatting",
    title: "Auto-format Indian phone numbers",
    description: "Strip country code, spaces, dashes. Normalize to 10-digit format",
    enabled: true,
  },
  {
    id: "source-tagging",
    title: "Auto-tag lead source",
    description: "Automatically set source field based on which integration received the lead",
    enabled: true,
  },
  {
    id: "required-fields",
    title: "Reject leads missing required fields",
    description: "Leads without name AND phone are rejected and logged as failed",
    enabled: true,
  },
  {
    id: "business-hours",
    title: "Queue leads outside business hours",
    description:
      "Hold leads received outside 9AM-7PM for assignment next business day",
    enabled: false,
  },
];

const duplicateActions = ["Flag as duplicate", "Merge automatically", "Create anyway"];

const sourceTags = [
  { label: "IndiaMart", color: "bg-orange-100 text-orange-700" },
  { label: "JustDial", color: "bg-blue-100 text-blue-700" },
  { label: "Website", color: "bg-purple-100 text-purple-700" },
  { label: "WhatsApp", color: "bg-green-100 text-green-700" },
  { label: "Manual", color: "bg-gray-100 text-gray-600" },
];

export function LeadNormalizationSection() {
  const { showToast } = useToast();
  const [rules, setRules] = useState(initialRules);
  const [duplicateAction, setDuplicateAction] = useState("Flag as duplicate");

  const toggleRule = (id: string) => {
    setRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r))
    );
  };

  const handleSave = () => {
    showToast("Normalization rules saved successfully", "success");
  };

  return (
    <div className="bg-white rounded-xl border border-[#e5e7eb] p-5">
      <h3 className="text-lg font-semibold text-[#111827]">Lead Normalization Rules</h3>
      <p className="text-sm text-[#6b7280] mt-0.5 mb-5">
        How incoming leads from all sources are cleaned and standardized before entering CRM
      </p>

      {/* Info Banner */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-5">
        <div className="flex items-start gap-2">
          <i className="ri-information-line text-indigo-600 text-sm mt-0.5" />
          <p className="text-xs text-indigo-700 leading-relaxed">
            All leads pass through the normalization layer before assignment. Rules apply to every source
            automatically.
          </p>
        </div>
      </div>

      {/* Rule 1: Duplicate Detection */}
      <div className="pb-4 mb-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <button
            onClick={() => toggleRule("duplicate-detection")}
            className={`relative mt-0.5 w-9 h-5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${
              rules.find((r) => r.id === "duplicate-detection")?.enabled ? "bg-indigo-600" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                rules.find((r) => r.id === "duplicate-detection")?.enabled ? "left-4" : "left-0.5"
              }`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-[#111827]">
                  {rules.find((r) => r.id === "duplicate-detection")?.title}
                </p>
                <p className="text-sm text-[#6b7280] mt-0.5">
                  {rules.find((r) => r.id === "duplicate-detection")?.description}
                </p>
              </div>
              <select
                value={duplicateAction}
                onChange={(e) => setDuplicateAction(e.target.value)}
                className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-[#374151] bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent flex-shrink-0"
              >
                {duplicateActions.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Rule 2: Phone Formatting */}
      <div className="pb-4 mb-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <button
            onClick={() => toggleRule("phone-formatting")}
            className={`relative mt-0.5 w-9 h-5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${
              rules.find((r) => r.id === "phone-formatting")?.enabled ? "bg-indigo-600" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                rules.find((r) => r.id === "phone-formatting")?.enabled ? "left-4" : "left-0.5"
              }`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-[#111827]">
                  {rules.find((r) => r.id === "phone-formatting")?.title}
                </p>
                <p className="text-sm text-[#6b7280] mt-0.5">
                  {rules.find((r) => r.id === "phone-formatting")?.description}
                </p>
              </div>
              <span className="inline-flex items-center px-2 py-1 rounded text-xs text-[#374151] bg-gray-100 whitespace-nowrap flex-shrink-0">
                +91 98765-43210 → 9876543210
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Rule 3: Source Tagging */}
      <div className="pb-4 mb-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <button
            onClick={() => toggleRule("source-tagging")}
            className={`relative mt-0.5 w-9 h-5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${
              rules.find((r) => r.id === "source-tagging")?.enabled ? "bg-indigo-600" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                rules.find((r) => r.id === "source-tagging")?.enabled ? "left-4" : "left-0.5"
              }`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#111827]">
              {rules.find((r) => r.id === "source-tagging")?.title}
            </p>
            <p className="text-sm text-[#6b7280] mt-0.5">
              {rules.find((r) => r.id === "source-tagging")?.description}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {sourceTags.map((tag) => (
                <span
                  key={tag.label}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${tag.color}`}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Rule 4: Required Fields */}
      <div className="pb-4 mb-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <button
            onClick={() => toggleRule("required-fields")}
            className={`relative mt-0.5 w-9 h-5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${
              rules.find((r) => r.id === "required-fields")?.enabled ? "bg-indigo-600" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                rules.find((r) => r.id === "required-fields")?.enabled ? "left-4" : "left-0.5"
              }`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-[#111827]">
                  {rules.find((r) => r.id === "required-fields")?.title}
                </p>
                <p className="text-sm text-[#6b7280] mt-0.5">
                  {rules.find((r) => r.id === "required-fields")?.description}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600 border border-red-200 whitespace-nowrap">
                  Name (required)
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-600 border border-red-200 whitespace-nowrap">
                  Phone (required)
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-[#6b7280] whitespace-nowrap">
                  Email (optional)
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-[#6b7280] whitespace-nowrap">
                  Message (optional)
                </span>
              </div>
            </div>
            <a
              href="#"
              className="inline-block text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-2"
              onClick={(e) => {
                e.preventDefault();
                showToast("Opening field customization...", "info");
              }}
            >
              Customize Fields
            </a>
          </div>
        </div>
      </div>

      {/* Rule 5: Business Hours */}
      <div className="pb-4 mb-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => toggleRule("business-hours")}
            className={`relative mt-0.5 w-9 h-5 rounded-full transition-colors flex-shrink-0 cursor-pointer ${
              rules.find((r) => r.id === "business-hours")?.enabled ? "bg-indigo-600" : "bg-gray-300"
            }`}
          >
            <div
              className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                rules.find((r) => r.id === "business-hours")?.enabled ? "left-4" : "left-0.5"
              }`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-[#111827]">
                  {rules.find((r) => r.id === "business-hours")?.title}
                </p>
                <p className="text-sm text-[#6b7280] mt-0.5">
                  {rules.find((r) => r.id === "business-hours")?.description}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm text-[#374151]">9:00 AM — 7:00 PM IST Mon-Sat</p>
                <a
                  href="#"
                  className="inline-block text-xs text-indigo-600 hover:text-indigo-700 font-medium mt-1"
                  onClick={(e) => {
                    e.preventDefault();
                    showToast("Opening hours configuration...", "info");
                  }}
                >
                  Edit Hours
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        className="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap mt-2"
      >
        Save Normalization Rules
      </button>
    </div>
  );
}